import * as React from 'react';
import {useContext, useEffect, useRef, useState} from 'react';
import {Col, Form, InputRef, notification, Row, Space, Tag, TreeSelect} from 'antd';
import {useNavigate, useParams} from "react-router-dom";
import axiosApiInstance from '../../../api/axiosClient';
import {
    ModalForm,
    ProFormDigit,
    ProFormGroup,
    ProFormInstance,
    ProFormList,
    ProFormSelect,
    ProFormText,
    ProFormTextArea
} from "@ant-design/pro-components";
import {PageLoading} from "@ant-design/pro-layout";
// axiosApiInstance imported above
import {GlobalStateContext} from "../../../context/GlobalContext";
import type {DraggableData, DraggableEvent} from 'react-draggable';
import {useAtom, useAtomValue} from "jotai";
// import {creatorCategoriesAtom, creatorsAtom, creatorTagsAtom, tagOptionsAtom} from "./atoms";
import {creatorAtom, creatorTagsAtomById,} from "./atoms";
import {queryClientAtom} from "jotai-tanstack-query";
import {ProFormTreeSelect} from "@ant-design/pro-form/lib";
import {getPaletteColor} from "./utils";
import {generate} from "@ant-design/colors";
import {formatSocialMediaAccountName} from "./utils";
import ContactLinkField, { ContactLinkMigrationAlert } from "./ContactLinkField";
import CreatorDetailContent from './CreatorDetailContent';
// import TagOptions = API.Creators.TagOptions;

type Props = {
    editable: boolean
};

export type TagCategoryValue = {
    label: string;
    value: string;
    checked: boolean;
    color: string;
}

export type TagCategory = {
    label: string;
    value: string;
    checkable: boolean;
    allowMultiple: boolean;
    children: TagCategoryValue[];
}

// export type TagOptions = {
//     label: string;
//     value: number;
//     color: string;
//     checkable: boolean;
//     children: TagOptions[];
// }

// Extract initially selected values from tagOptions
export const getInitialSelectedValues = (options: TagCategory[]): string[] => {

    if (!options) {
        return []
    }

    const selectedValues: string[] = [];
    options.forEach(category => {
        category.children.forEach(child => {
            if (child.checked) {
                selectedValues.push(child.value);
            }
        });
    });
    // console.log({selectedValues})
    return selectedValues;
};

const CreatorViewEdit = (props: Props) => {
    const [form] = Form.useForm<API.Creators.Creator>();
    const navigate = useNavigate();
    const routerParams = useParams();
    const [loading, setLoading] = useState<boolean>(false);
    const formRef = useRef<ProFormInstance>();
    const inputRef = useRef<InputRef>(null);
    // const categoriesRef = useRef<BaseSelect>(null);
    // const [categoriesData, setCategoriesData] = useState<API.Creators.CreatorContentCategory[] | undefined>([]);
    const {
        employees,
    } = useContext(GlobalStateContext);
    // watch for changes of managed_by_employee_id field
    // const managed_by_employee_id = Form.useWatch('managed_by_employee_id', form);
    const queryClient = useAtomValue(queryClientAtom);
    const {id} = useParams();
    // const [{data: creators}] = useAtom(creatorsAtom);
    const [{data: tagOptions}] = useAtom(creatorTagsAtomById(id));
    const [{data: creator}] = useAtom(creatorAtom(id));
    // const [tagOptions, setTagOptions([])

    // Transform and sort tag options: rename "Specific" to "Content Category" and prioritize categories
    const transformedTagOptions = React.useMemo(() => {
        if (!tagOptions) return [];
        
        // Rename "Specific" to "Content Category" for display
        const transformed = tagOptions.map(option => ({
            ...option,
            label: option.label === 'Specific' ? 'Content Category' : option.label
        }));
        
        // Sort to show Prime Channel Category and Content Category first
        const sorted = [...transformed].sort((a, b) => {
            const priorityCategories = ['Prime Channel Category', 'Content Category','Channel Type'];
            const aIndex = priorityCategories.indexOf(a.label);
            const bIndex = priorityCategories.indexOf(b.label);
            
            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;
            return 0;
        });
        
        return sorted;
    }, [tagOptions]);

    const {SHOW_PARENT} = TreeSelect;


    useEffect(() => {
        setFormData()
    }, [id, creator]);

    const setFormData = () => {
        if (!creator) return;
        form.setFieldsValue(creator)
    }

    const [bounds, setBounds] = useState({left: 0, top: 0, bottom: 0, right: 0});
    const draggleRef = useRef<HTMLDivElement>(null);
    const [disabled, setDisabled] = useState(true);

    const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
        const {clientWidth, clientHeight} = window.document.documentElement;
        const targetRect = draggleRef.current?.getBoundingClientRect();
        if (!targetRect) {
            return;
        }
        setBounds({
            left: -targetRect.left + uiData.x,
            right: clientWidth - (targetRect.right - uiData.x),
            top: -targetRect.top + uiData.y,
            bottom: clientHeight - (targetRect.bottom - uiData.y),
        });
    };

    const [timeoutId, setTimeoutId] = useState(null);
    const [activeTab, setActiveTab] = useState<'info' | 'yt' | 'ig' | 'tt'>('info');

    const handleValidationYTVideo = (rule, value): Promise<void | any> => {
        if (value) {
            if (value < 200) {
                return Promise.reject("Price seems lower than usual..");
            } else if (value > 30000) {
                return Promise.reject("Price seems higher than usual..");
            } else {
                return Promise.resolve();
            }
        } else {
            return Promise.resolve();
        }
    };

    const handleValidationTTVideo = (rule, value): Promise<void | any> => {

        // Your validation logic here
        if (value) {
            if (value < 40) {
                // callback("Price seems lower than usual..");
                return Promise.reject("Price seems lower than usual..");
            } else if (value > 8000) {
                // callback("Price seems higher than usual..");
                return Promise.reject("Price seems higher than usual..");
            } else {
                // callback();
                return Promise.resolve();
            }
        } else {
            // callback();
            return Promise.resolve();
        }

    };

    const handleValidationIGReel = (rule, value) => {
        if (value) {
            if (value < 40) {
                return Promise.reject("Price seems lower than usual..");
            } else if (value > 8000) {
                return Promise.reject("Price seems higher than usual..");
            } else {
                return Promise.resolve();
            }
        } else {
            return Promise.resolve();
        }
    };

    const handleValidationIGPost = (rule, value) => {
        if (value) {
            if (value < 20) {
                return Promise.reject("Price seems lower than usual..");
            } else if (value > 5000) {
                return Promise.reject("Price seems higher than usual..");
            } else {
                return Promise.resolve();
            }
        } else {
            return Promise.resolve();
        }

    };

    const handleValidationIGStory = (rule, value) => {
        if (value) {
            if (value < 20) {
                return Promise.reject("Price seems lower than usual..");
            } else if (value > 2000) {
                return Promise.reject("Price seems higher than usual..");
            } else {
                return Promise.resolve();
            }
        } else {
            return Promise.resolve();
        }
    };

    const [selectedValues, setSelectedValues] = useState<string[]>(getInitialSelectedValues(tagOptions));

    const handleChange = (values: string[]) => {
        let newSelectedValues = [...values];

        transformedTagOptions.forEach(category => {
            if (!category.allowMultiple) {
                const selectedChildren = newSelectedValues.filter(value =>
                    category.children.some(child => child.value === value)
                );
                if (selectedChildren.length > 1) {
                    // Keep only the most recently selected child
                    const lastSelectedChild = selectedChildren.pop();
                    newSelectedValues = newSelectedValues.filter(value =>
                        !category.children.some(child => child.value === value)
                    );
                    if (lastSelectedChild !== undefined) {
                        newSelectedValues.push(lastSelectedChild);
                    }
                }
            }
        });
        // console.log({newSelectedValues})
        setSelectedValues(newSelectedValues);
    };

    const tagRender = (props: any) => {
        const {label, value, closable, onClose} = props;
        // console.log({label, value, closable, onClose})

        const findColor = (options: TagCategory[], value: string): string | undefined => {
            // console.log({options, value})
            for (const option of options) {
                if (option.value === value) return undefined;
                const found = option.children.find(child => child.value === value);
                if (found) return found.color;
            }
            return undefined;
        };

        const tagColor = findColor(transformedTagOptions, value);
        // if (!tagColor) return null;
        const palette = tagColor ? generate(tagColor) : [];

        // console.log({tagColor, palette})

        return (
            <Tag
                color={tagColor}
                closable={closable}
                onClose={onClose}
                style={{marginRight: 3, color: getPaletteColor(tagColor, palette)}} // Apply text color
            >
                {label}
            </Tag>
        );
    };

    const S_TITLE: React.CSSProperties = {
        fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase',
        letterSpacing: '0.5px', paddingBottom: 10, marginBottom: 14, borderBottom: '1px solid var(--surface-border)',
    };
    const S_CARD: React.CSSProperties = {
        border: '1px solid var(--surface-border)', borderRadius: 8, padding: '16px 20px', background: 'var(--surface)', marginBottom: 12,
    };

    return (
        <ModalForm<API.Creators.Creator>
            open
            form={form}
            // formRef={formRef}
            autoFocusFirstInput
            width={1000}
            submitter={props.editable ? {} : false}
            // submitter={{render: (_, dom) => props.editable ? dom : null}}
            initialValues={creator}
            onVisibleChange={(visible) => {
                if (visible) setFormData();
            }}
            modalProps={{
                destroyOnClose: true,
                okText: 'Save',
                okButtonProps: {hidden: !props.editable, loading: loading},
                onCancel: () => navigate(`/creators/list`),
            }}
            onFinish={async (formData) => {
                console.log(formData);
                // return true
                return axiosApiInstance.put(`/api/creators/items/${routerParams.id}`, formData)
                    .then(() => {
                        notification.success({message: 'Success'});
                        queryClient.invalidateQueries({queryKey: ['creators']});
                        return true;
                    })
                    .catch((error) => {
                        notification.error({message: 'Error'});
                        console.error(error);
                        return false;
                    });
            }}
            validateMessages={{required: "${label} is required!"}}
            readonly={!props.editable}
            requiredMark={props.editable}
        >

            {loading ? <PageLoading/> :
                <>
                    {/* View mode — rendered by shared component */}
                    {!props.editable && <CreatorDetailContent id={id} />}

                    {/* Edit mode form */}
                    <div style={{ display: props.editable ? 'block' : 'none' }}>





                    <ProFormDigit
                        name="id"
                        hidden={true}
                    />



                    {/* ── Edit-mode form fields ── */}
                    {props.editable && (
                        <>
                        <ContactLinkMigrationAlert />
                        <div style={S_CARD}>
                            <div style={S_TITLE}>Creator Info</div>
                            <ProFormGroup>
                                <ProFormText
                                    width="lg"
                                    name="creator"
                                    label="Creator"
                                    rules={[{required: true}]}
                                />
                            </ProFormGroup>

                            <ProFormGroup>
                                <ProFormSelect
                                    width="xs"
                                    name="main_platform_id"
                                    label="Main Platform"
                                    options={[{label: 'YouTube', value: 1}, {label: 'Instagram', value: 2}, {
                                        label: 'TikTok',
                                        value: 3
                                    }, {label: 'Unknown', value: 4}]}
                                    rules={[{required: true}]}
                                />

                                <ProFormTreeSelect
                                    width={"lg"}
                                    label={"Tags"}
                                    disabled={!props.editable}
                                    name="tags"
                                    normalize={(value, prevValue, allValues) => {
                                        return value.map(val => ({
                                            tag_category_id: parseInt(val.split(",")[0]),
                                            tag_category_value_id: parseInt(val.split(",")[1])
                                        }))
                                    }}
                                    fieldProps={{
                                        showCheckedStrategy: SHOW_PARENT,
                                        multiple: true,
                                        tagRender: (props) => tagRender(props),
                                        treeCheckable: true,
                                        treeExpandAction: "click",
                                        value: selectedValues,
                                        onChange: handleChange,
                                        treeData: transformedTagOptions,
                                        filterTreeNode: (inputValue: string, treeNode: any) =>
                                            String(treeNode.label ?? treeNode.title ?? '').toLowerCase().includes(inputValue.toLowerCase()),
                                    }}
                                />
                            </ProFormGroup>

                            <ProFormGroup>
                                <ProFormTextArea
                                    width="md"
                                    name="reason"
                                    label="Comments"
                                />
                            </ProFormGroup>
                        </div>
                        </>
                    )}

                    {props.editable && (
                    <div style={S_CARD}>
                        <div style={S_TITLE}>Linked Connections</div>
                        <ContactLinkField
                            editable={props.editable}
                            initialContacts={creator?.contacts ?? null}
                        />
                    </div>
                    )}

                    {props.editable && (
                    <div style={S_CARD}>
                        <div style={S_TITLE}>Social Media Accounts</div>
                        <ProFormList name="accounts"
                                     copyIconProps={{}} deleteIconProps={{}}
                                     creatorButtonProps={{creatorButtonText: 'Add account'}}
                                     actionRender={(field, action, defaultActionDom) => [
                                         ...defaultActionDom,
                                         // !props.editable && <Tooltip title={'Fetch follower count'}>
                                         //     <Button icon={<DownloadOutlined/>} type={"link"}
                                         //             onClick={() => updateAccountFollowerCount(field)}/>
                                         // </Tooltip>
                                     ]}
                        >
                            {(f, index, action) => {
                                return (
                                    <ProFormGroup>
                                        <ProFormDigit
                                            name="id"
                                            hidden={true}
                                        />

                                        <ProFormDigit
                                            name="creator_id"
                                            hidden={true}
                                        />

                                        <ProFormSelect
                                            // width="xs"
                                            wrapperCol={{style: {width: 70}}}
                                            name="platform_id"
                                            label="Platform"
                                            // valueEnum={
                                            //     {
                                            //         1: {
                                            //             text: 'YouTube',
                                            //         }
                                            //     }}
                                            options={[{label: 'YouTube', value: 1}, {
                                                label: 'Instagram',
                                                value: 2
                                            }, {label: 'TikTok', value: 3},]}
                                            rules={[{required: true}]}
                                        />


                                        <ProFormText
                                            // width={"md"}
                                            wrapperCol={{style: {width: 430}}}
                                            label="Key"
                                            name="key"
                                            readonly={!!form.getFieldValue(['accounts', index, 'id'])}
                                            tooltip={form.getFieldValue(['accounts', index, 'id']) ? 'Delete and recreate to change URL' : undefined}
                                        />

                                        <ProFormDigit
                                            name="following"
                                            label={'Following'}
                                            fieldProps={{precision: 0}}
                                            // width={"xs"}
                                            wrapperCol={{style: {width: 80}}}
                                            readonly={true}
                                            tooltip={(() => {
                                                const lastFetch = form.getFieldValue(['accounts', index, 'last_fetch_at']);
                                                if (lastFetch) {
                                                    const date = new Date(lastFetch);
                                                    const now = new Date();
                                                    const diffMs = now.getTime() - date.getTime();
                                                    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                                                    if (diffDays === 0) return `Updated today`;
                                                    if (diffDays === 1) return `Updated yesterday`;
                                                    return `Updated ${diffDays} days ago`;
                                                }
                                                return 'Never updated';
                                            })()}
                                        />

                                        {/*<ProFormSwitch*/}
                                        {/*    name="auto_fetch_followers"*/}
                                        {/*    label={'Auto Fetch Followers'}*/}
                                        {/*    // width={"xs"}*/}
                                        {/*    hidden={!props.editable}*/}
                                        {/*    wrapperCol={{style: {width: 150}}}*/}
                                        {/*/>*/}


                                    </ProFormGroup>
                                );
                            }}
                        </ProFormList>
                    </div>)}



                    {props.editable && (
                    <div style={S_CARD}>
                        <div style={S_TITLE}>Pricings</div>

                        <Row justify="space-between">
                            <Space wrap size={24}>
                                <Col>
                                    <ProFormDigit
                                        name={["pricing", "youtube_video"]}
                                        label="Price YouTube Video"
                                        fieldProps={{precision: 0}}
                                        width={"sm"}
                                        rules={[
                                            {
                                                validator: (rule, value) => {
                                                    return handleValidationYTVideo(rule, value);
                                                },
                                                type: "number",
                                                warningOnly: true,
                                                required: true,
                                            },
                                        ]}
                                    />
                                </Col>

                                <Col>
                                    <ProFormDigit
                                        name={["pricing", "tiktok_video"]}
                                        label="Price TikTok Video"
                                        fieldProps={{precision: 0}}
                                        width={"sm"}
                                        rules={[
                                            {
                                                // validator: (rule, value, callback) => {
                                                //     handleValidationTTVideo(rule, value, callback);
                                                // },
                                                validator: (rule, value) => {
                                                    return handleValidationTTVideo(rule, value);
                                                },
                                                type: "number",
                                                warningOnly: true,
                                                required: true,
                                            },
                                        ]}
                                    />
                                </Col>
                            </Space>
                        </Row>

                        <Row justify="space-evenly" style={{width: '100%'}}>
                            <Space wrap size={24}>
                                <Col>
                                    <ProFormDigit
                                        name={["pricing", "instagram_reel"]}
                                        label="Price Instagram Reel"
                                        fieldProps={{precision: 0}}
                                        width={"sm"}
                                        rules={[
                                            {
                                                validator: (rule, value) => {
                                                    return handleValidationIGReel(rule, value);
                                                },
                                                type: "number",
                                                warningOnly: true,
                                                required: true,
                                            },
                                        ]}
                                    />
                                </Col>

                                <Col>
                                    <ProFormDigit
                                        name={["pricing", "instagram_post"]}
                                        label="Price Instagram Post"
                                        fieldProps={{precision: 0}}
                                        width={"sm"}
                                        rules={[
                                            {
                                                validator: (rule, value) => {
                                                    return handleValidationIGPost(rule, value);
                                                },
                                                type: "number",
                                                warningOnly: true,
                                                required: true,
                                            },
                                        ]}
                                    />
                                </Col>

                                <Col>
                                    <ProFormDigit
                                        name={["pricing", "instagram_story"]}
                                        label="Price Instagram Story"
                                        fieldProps={{precision: 0}}
                                        width={"sm"}
                                        rules={[
                                            {
                                                validator: (rule, value) => {
                                                    return handleValidationIGStory(rule, value);
                                                },
                                                type: "number",
                                                warningOnly: true,
                                                required: true,
                                            },
                                        ]}
                                    />
                                </Col>
                            </Space>
                        </Row>

                        <Row justify="space-evenly" style={{width: '100%'}}>
                            <Space wrap size={24}>
                                <Col>
                                    <ProFormDigit
                                        name={["pricing", "exclusivity"]}
                                        label="Exclusivity"
                                        fieldProps={{precision: 0}}
                                        width={"sm"}
                                    />
                                </Col>

                                <Col>
                                    <ProFormDigit
                                        name={["pricing", "paid_ads_sponsorship"]}
                                        label="Paid Ads/Sponsorship"
                                        fieldProps={{precision: 0}}
                                        width={"sm"}
                                    />
                                </Col>

                                <Col>
                                    <ProFormDigit
                                        name={["pricing", "collaboration"]}
                                        label="Collaboration"
                                        fieldProps={{precision: 0}}
                                        width={"sm"}
                                    />
                                </Col>
                            </Space>
                        </Row>

                    </div>)}               </div>{/* end info tab wrapper */}

                </>
            }

        </ModalForm>
    );
};

export default CreatorViewEdit;